import {
  ReflectionsResponse,
  assessmentQuestionnaireApiHandler,
} from '@api/assessmentQuestionnaireApiHandler';
import { ApiResponse } from '@interfaces/index';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { SystemButton } from '@shared/molecules';
import { CoreLayout } from '@shared/templates';
import { useQuery } from '@tanstack/react-query';
import { formatDateToDateFullMonthYear } from '@utils/date.util';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import styles from './styles.module.scss';

const LoadingSkeleton = () => (
  <Timeline position="alternate">
    {[1, 2, 3, 4, 5, 6].map((item) => (
      <TimelineItem key={`skeleton-${item}`}>
        <TimelineOppositeContent sx={{ m: 'auto 0' }}>
          <Skeleton
            variant="text"
            width={120}
            height={24}
            sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
          />
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineConnector />
          <TimelineDot>
            <Skeleton
              variant="circular"
              width={24}
              height={24}
              sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
            />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent sx={{ py: '12px', px: 2 }}>
          <Skeleton
            variant="text"
            width="100%"
            height={200}
            sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
          />
        </TimelineContent>
      </TimelineItem>
    ))}
  </Timeline>
);

export const ReflectionsPage: React.FC = () => {
  const history = useHistory();
  const [reflections, setReflections] = useState<ReflectionsResponse[] | []>([]);

  const fetchAllUserReflections = async (): Promise<ApiResponse<ReflectionsResponse[]>> =>
    assessmentQuestionnaireApiHandler.getReflections();

  const {
    data: reflectionsData,
    isLoading: reflectionsLoading,
    error: reflectionsErrors,
  } = useQuery({
    queryKey: ['fetchAllUserReflections'],
    queryFn: fetchAllUserReflections,
    enabled: true,
  });

  useEffect(() => {
    if (reflectionsData?.success) {
      const sortedReflections = [...reflectionsData.responseObject].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setReflections(sortedReflections);
    } else {
      setReflections([]);
    }
  }, [reflectionsData]);

  return (
    <CoreLayout title="Reflections Page" navigationType="default">
      <div className="container" role="main">
        <div className={styles.reflectionsContainer}>
          <header className={styles.title}>
            <h1 id="page-title">Journals</h1>
            <SystemButton
              onClick={() => history.push('/journal')}
              className={styles.createReflectionBtn}
              aria-label="Create new reflection"
            >
              Create Journal
            </SystemButton>
          </header>

          {reflectionsLoading ? (
            <Box aria-label="Loading reflections" aria-live="polite">
              <LoadingSkeleton />
            </Box>
          ) : reflections?.length === 0 || reflectionsErrors ? (
            <div className={styles.noDataSection} aria-live="polite">
              <p>No reflections found</p>
            </div>
          ) : (
            <Timeline position="alternate" aria-labelledby="page-title" sx={{ padding: 0 }}>
              {reflections?.map((refl, index) => (
                <TimelineItem
                  key={index}
                  tabIndex={0}
                  aria-label={`Reflection ${index + 1} from ${
                    refl?.createdAt
                      ? formatDateToDateFullMonthYear(refl?.createdAt)
                      : 'unknown date'
                  }`}
                >
                  <TimelineOppositeContent
                    sx={{ m: 'auto 0' }}
                    align="right"
                    variant="body2"
                    color="text.secondary"
                  >
                    {refl?.createdAt ? formatDateToDateFullMonthYear(refl?.createdAt) : 'No date'}
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineConnector sx={{ bgcolor: '#3fb9fc' }} />
                    <TimelineDot
                      variant="outlined"
                      sx={{ border: '2px solid #019df2' }}
                      aria-hidden="true"
                    >
                      <MenuBookIcon sx={{ color: '#019df2' }} />
                    </TimelineDot>
                    <TimelineConnector sx={{ bgcolor: '#3fb9fc' }} />
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    {/* TODO: ADD TITLE IN THE FUTURE */}
                    {/* <Typography variant="h6" component="h2">
                      {`Reflection ${index + 1}`}
                    </Typography> */}
                    <Typography className={styles.reflectionText} component="blockquote">
                      {refl?.reflection || 'No reflection text'}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          )}
        </div>
      </div>
    </CoreLayout>
  );
};
